import type { LangCode } from "@/api/types";

import { EditableSection } from "./EditableSection";
import { Field } from "./Field";
import type { RecordTabFormProps } from "./shared";

/** Форма для item_type=abbreviation — две пары:
 *  EN: сокращение + расшифровка
 *  RU: сокращение + расшифровка (перевод)
 *  + блок «Контекст» с полисемией / клинреками / примерами. */
export function AbbreviationForm({
  item,
  payload,
  editMode,
  draft,
  onChange,
  onCancelEdit,
}: RecordTabFormProps) {
  const srcLang = (item.source_lang ?? null) as LangCode | null;
  const tgtLang = (item.target_lang ?? null) as LangCode | null;

  const srcAbbrValue =
    editMode && draft.source_abbreviation != null
      ? draft.source_abbreviation
      : (payload.source_abbreviation ?? payload.abbreviation ?? item.source_text ?? "");
  const srcExpansionValue =
    editMode && draft.source_expansion != null
      ? draft.source_expansion
      : (payload.source_expansion ?? payload.definition ?? "");
  const tgtAbbrValue =
    editMode && draft.target_abbreviation != null
      ? draft.target_abbreviation
      : (payload.target_abbreviation ?? "");
  const tgtExpansionValue =
    editMode && draft.target_expansion != null
      ? draft.target_expansion
      : (payload.target_expansion ?? "");

  return (
    <>
      <EditableSection
        title="Сокращение в источнике"
        editMode={editMode}
        draftCount={Object.keys(draft).length}
        onCancelEdit={onCancelEdit}
      >
        <div className="rf-form-grid">
          <Field
            fieldKey="source_abbreviation"
            label="Сокращение"
            langTag={srcLang}
            value={srcAbbrValue}
            mono
            editable={editMode}
            onChange={onChange}
          />
          <Field
            fieldKey="source_expansion"
            label="Расшифровка"
            langTag={srcLang}
            value={srcExpansionValue}
            editable={editMode}
            keepEmpty
            onChange={onChange}
          />
        </div>
      </EditableSection>

      <section className="rf-section">
        <div className="rf-section__head">
          <span>Перевод на {tgtLang === "ru" ? "RU" : (tgtLang ?? "—")}</span>
        </div>
        <div className="rf-section__body">
          <div className="rf-form-grid">
            <Field
              fieldKey="target_abbreviation"
              label="Сокращение"
              langTag={tgtLang}
              value={tgtAbbrValue}
              mono
              editable={editMode}
              onChange={onChange}
            />
            <Field
              fieldKey="target_expansion"
              label="Расшифровка"
              langTag={tgtLang}
              value={tgtExpansionValue}
              editable={editMode}
              keepEmpty
              onChange={onChange}
            />
          </div>
        </div>
      </section>

      <AbbreviationContext payload={payload} item={item} />
    </>
  );
}

function AbbreviationContext({
  payload,
  item,
}: {
  payload: RecordTabFormProps["payload"];
  item: RecordTabFormProps["item"];
}) {
  const isAmbiguous = Boolean(payload.is_ambiguous);
  const competing = payload.competing_senses ?? [];
  const variants = payload.surface_variants ?? [];
  const clinrecIds = payload.clinrec_ids ?? [];
  const clinrecTitles = payload.clinrec_titles ?? [];
  const mkbCodes = payload.mkb_codes ?? [];
  const examples = payload.examples ?? [];
  const reviewReason = payload.review_reason;
  const occurrenceCount = payload.raw_occurrence_count;

  const hasContext =
    isAmbiguous ||
    competing.length > 0 ||
    variants.length > 0 ||
    clinrecIds.length > 0 ||
    examples.length > 0 ||
    Boolean(reviewReason) ||
    occurrenceCount != null ||
    mkbCodes.length > 0 ||
    Boolean(item.source_key);
  if (!hasContext) return null;

  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>Контекст</span>
        {isAmbiguous && (
          <span className="mt-badge mt-badge--warning mt-badge--no-dot">
            полисемия
          </span>
        )}
      </div>
      <div className="rf-section__body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(160px, max-content) 1fr",
            gap: "4px 12px",
            fontSize: "var(--fs-sm)",
          }}
        >
          {reviewReason && <Row k="Причина" v={reviewReason} />}
          {occurrenceCount != null && (
            <Row k="Встречается" v={`${occurrenceCount} раз(а)`} />
          )}
          {variants.length > 0 && (
            <Row k="Варианты написания" v={variants.join(" · ")} />
          )}
          {competing.length > 0 && (
            <Row
              k="Конкурирующие смыслы"
              v={competing
                .map(
                  (s) =>
                    (s as { sense_label?: string; definition?: string })
                      .sense_label ??
                    (s as { definition?: string }).definition ??
                    JSON.stringify(s),
                )
                .join(" · ")}
            />
          )}
          {clinrecTitles.length > 0 && (
            <Row k="Клинреки" v={clinrecTitles.join(" · ")} />
          )}
          {clinrecIds.length > 0 && clinrecTitles.length === 0 && (
            <Row k="Клинреки (id)" v={clinrecIds.join(", ")} />
          )}
          {mkbCodes.length > 0 && (
            <Row k="МКБ" v={mkbCodes.join(", ")} />
          )}
          {item.source_key && <Row k="source_key" v={item.source_key} />}
        </div>
        {examples.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div
              className="mt-text-tight mt-muted"
              style={{ marginBottom: 6 }}
            >
              Примеры
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {examples.slice(0, 5).map((ex, i) => (
                <div
                  key={i}
                  className="rf-example"
                  style={{ fontSize: "var(--fs-sm)" }}
                >
                  {typeof ex === "string"
                    ? ex
                    : ((ex as { sentence?: string; text?: string }).sentence ??
                      (ex as { text?: string }).text ??
                      JSON.stringify(ex))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Row({ k, v }: { k: string; v: unknown }) {
  return (
    <>
      <div className="mt-mono mt-text-tight mt-muted">{k}</div>
      <div style={{ overflowWrap: "anywhere" }}>{String(v)}</div>
    </>
  );
}
